/**
 * Values for different telemetry information types.
 */
enum InformationTypes {
  Information = 0,
  Warning = 1
}

/** Type of behaviors allowed for page action events. Determined by CommonSchema.
 * reference: https://microsoft.sharepoint.com/sites/mscomdata/Pages/awa-behavior-dictionary.aspx
 */
enum PageActionBehavior {
  Unknown = 300, // Defines a behavior to allow for custom identification of user behaviors that are not covered elsewhere
  NavigateBack = 1, // ​Defines user page actions on content for the purpose of advancing to the previous index position within a webpage
  NavigationForward = 2, // ​Defines user page actions on content for the purpose of advancing to a specific index position within a webpage
  Navigate = 3, // ​Defines user page actions on content for the purpose of advancing to a specific index position within a webpage
  PageScroll = 0, // Defines a behavior to identify all Page View and Content Update events.
  Search = 61, // ​Defines user page actions for the purpose of submitting a search query
  SearchAutoComplete = 60, // ​Defines page actions for the purpose of auto-completing a search query during user input
  Show = 14, // Defines user page action for interacting with a page element that shows content
  Expand = 7, // ​Defines user page actions for the purpose of expanding content or content container
  Reduce = 8, // ​Defines user page actions for the purpose of reducing content or content container
  Purchase = 80, // ​Defines user page actions for the purpose of purchasing physical or digital goods or services from a Microsoft online storefront
  SignUserIn = 100, // ​Defines user page actions for the purpose of user sign-in
  LogUserOut = 101, // ​Defines user page actions for the purpose of user sign-out
  ReadMore = 7, // ​Defines user page actions for the purpose of expanding content or content container
  ReadLess = 8, // ​Defines user page actions for the purpose of expanding content or content container
  Vote = 140, // ​Defines user page actions for the purpose of rating content or voting for content
  ShowFlyout = 300, // wamoha: This is redundant. Not sure where this is being used.
  StartProcess = 20, // ​Defines user page actions that initiate a web process unique to adopter
  ProcessCheckpoint = 21, // ​Defines user page actions that represents a checkpoint in a web process unique to adopter
  CompleteProcess = 22, // ​Defines user page actions that complete a web process unique to adopter
  CancelProcess = 23, // defines user page action for cancelling a process
  Apply = 4, // ​Defines user page actions for the purpose of applying filter(s) or making selections
  Remove = 5, // ​Defines user page actions for the purpose of applying filter(s) or making selections
  ReportReview = 144 // Defines a behavior for the purposes of a user reviewing a product or service
}

/**
 * different page action types
 * More tags are here - https://microsoft.sharepoint.com/sites/mscomdata/Pages/AWA-Action-Type-Dictionary.aspx
 */
const PageActionType = {
  click: "C",
  leftClick: "CL",
  rightClick: "CR",
  auto: "A",
  keyboard: "K",
  keyboardEnter: "KE",
  keyboardSpace: "KS",
  verticalScroll: "SV",
  other: "O"
};

/**
 * PageMetadata dictionary. More tags are here - https://microsoft.sharepoint.com/sites/mscomdata/Pages/awa-page-metadata-dictionary.aspx
 */
const PageMetadataTags = {
  audience: "aud", // ​string for describing the page's intended audience
  scenario: "scn", // ​string for describing the scenario a page is associated with in a multipage process
  scenarioStep: "scnstp", // ​string for describing a specific step a page is associated with in a multipage process
  scenarioError: "scnerror", // string for describing an error message that causes a specific step to fail
  type: "pageType", // ​string for describing page intent,
  view: "view", // ​string for describing page layout
  product: "product", // ​string for describing the Microsoft product the page is associated with. Use this tag only for non-purchase related scenarios. For PDP pages and to join with store data please use the ProductInfo blob documented on the Commerce scenario page. "Product" will not be available in the Adobe Analytics global reporting suite.
  productFamily: "prdct" // ​string for describing the product family the page is associated with
};

const EventsByName = {
  AgentFeedback: "MSEG.ServiceDesk.AgentFeedback",
  Error: "MSEG.ServiceDesk.Error",
  Information: "MSEG.ServiceDesk.Information",
  OutgoingRequest: "MSEG.ServiceDesk.OutgoingRequest",
  PageView: "MSEG.ServiceDesk.PageView"
};

/**
 * Valid values for an element's state in a user action event.
 */
export type ElementState = "unknown" | "collapsed" | "expanded" | "hidden";

export class GlobalTelemetryConstants {
  public readonly informationTypes: any;
  public readonly pageActionBehavior: any;
  public readonly pageActionType: any;
  public readonly pageMetadataTags: any;
  public readonly eventsByName: any;

  /**
   * An element's state is unknown.
   */
  public readonly unknown: ElementState = "unknown";
  /**
   * An element's state is collapsed.
   */
  public readonly collapsed: ElementState = "collapsed";
  /**
   * An element's state is expanded.
   */
  public readonly expanded: ElementState = "expanded";
  /**
   * An element's state is hidden.
   */
  public readonly hidden: ElementState = "hidden";

  public readonly maxDurationMsThreshold = 1000 * 60 * 5; // 5 minutes
  public readonly unKnown = "unknown";
  public readonly piiPlaceholder = "removed-to-prevent-pii";

  constructor() {
    this.informationTypes = InformationTypes;
    this.pageActionBehavior = PageActionBehavior;
    this.pageActionType = PageActionType;
    this.pageMetadataTags = PageMetadataTags;
    this.eventsByName = EventsByName;
  }
}
